import InputGroupContent from '@/components/elements/input'
import { Box, Flex, Heading } from '@chakra-ui/react'
import Submit from '@/components/elements/submit'
import TextareaGroupContent from '@/components/elements/textarea'
import { AppDispatch, RootState } from '@/store/store'
import { useDispatch, useSelector } from 'react-redux'
import Toast from '@/components/elements/toast'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import SpinnerLoading from '@/components/elements/spinner'
import { contactSchema } from '@/entities/schemas/contact'
import { yupResolver } from '@hookform/resolvers/yup'
import { Status } from '@/entities/api/users/contact'
import { updateContactById } from '@/services/api/customers/contact'
import { sessionRefrestUpdate } from '@/store/features/state'
import { sendEmail } from '@/services/api/mail/mail'
import { updateContactFn } from '@/store/features/contact'
import { useFormErrorToast } from '@/hooks/useFormErrorToast'

const OpenUsersRequest = ({ close }: { close: () => void }) => {
	const token = useSelector((state: RootState) => state.auth.token)
	const selectContact = useSelector(
		(state: RootState) => state.contacts.selectedContact
	)
	const [isLoading, setIsLoading] = useState<boolean>(false)
	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<{ response: string }>({
		resolver: yupResolver(contactSchema),
	})
	useFormErrorToast(errors)
	const dispatch = useDispatch<AppDispatch>()

	const onSubmit = async ({ response }: { response: string }) => {
		try {
			setIsLoading(true)

			const contactId = selectContact?._id ?? ''
			const contactEmail = selectContact?.email ?? ''
			const authToken = token ?? ''

			const contactUpdateData = {
				status: Status.viewed,
				response,
			}

			const [updateRes, emailRes] = await Promise.all([
				updateContactById(authToken, contactId, contactUpdateData),
				sendEmail(
					{
						to: contactEmail,
						subject: 'Respuesta a tu solicitud',
						message: response,
					},
					token ?? ''
				),
			])

			const updateStatus = updateRes?.status ?? 0
			const emailStatus = emailRes?.status ?? 0

			if (updateStatus === 401 || emailStatus === 401) {
				dispatch(sessionRefrestUpdate())
				return
			}

			if (
				[500, 404].includes(updateStatus) ||
				[500, 404].includes(emailStatus)
			) {
				Toast({
					type: 'error',
					message: 'Hubo un error al enviar la respuesta al usuario',
				})
				return
			}

			if (emailStatus === 406) {
				Toast({
					type: 'error',
					message:
						'El correo del usuario no es válido o no se pudo enviar el correo',
				})
				dispatch(updateContactFn({ ...selectContact, ...contactUpdateData }))
				setIsLoading(false)
				close?.()
				return
			}

			dispatch(updateContactFn({ ...selectContact, ...contactUpdateData }))
			Toast({
				type: 'success',
				message: 'Se envió correctamente la respuesta al usuario',
			})
			setIsLoading(false)
			close?.()
		} catch (error) {
			Toast({
				type: 'error',
				message: 'No se pudo enviar la respuesta al usuario',
			})
		} finally {
			setIsLoading(false)
		}
	}

	return (
		<form onSubmit={handleSubmit(onSubmit)}>
			<Flex
				w={{ base: 'full', sm: '80vw', md: '50vw', lg: '40vw', xl: '30vw' }}
				h='full'
				flexDirection='column'
				alignItems='center'
			>
				<Box
					w='full'
					display='flex'
					justifyContent='start'
					alignItems='center'
					mb='2'
				>
					<Heading
						as='h4'
						fontSize={{ base: 'lg', md: 'xl' }}
						color='black'
						fontWeight='900'
					>
						Informacion general
					</Heading>
				</Box>
				<InputGroupContent
					label='Nombre del usuario'
					readOnlyActive={true}
					defaultValue={selectContact?.fullname}
				/>
				<InputGroupContent
					label='Correo del usuario'
					readOnlyActive={true}
					defaultValue={selectContact?.email}
				/>
				<InputGroupContent
					label='Telefono del usuario'
					readOnlyActive={true}
					defaultValue={selectContact?.phone}
				/>
				<TextareaGroupContent
					label='Mensaje del usuario'
					readOnlyActive={true}
					defaultValue={selectContact?.message}
				/>
				<TextareaGroupContent
					label='Respuesta'
					placeholder='Ingresa la respuesta'
					name='response'
					required
					type='text'
					register={register}
					errors={errors}
				/>
				<Box pt='4' pb='8'>
					{isLoading ? (
						<SpinnerLoading color='black' />
					) : (
						<Submit text='Responder' />
					)}
				</Box>
			</Flex>
		</form>
	)
}

export default OpenUsersRequest
